import * as React from "react";
import * as userService from "../../../models/services/userService";
import * as calendarTaskService from "../../../models/services/calendarTaskService";
import { ICalendarTaskPage } from "source/models/calendarTask/calendarTaskPage";
import { Base } from "ui/Scripts/source/framework/base";
import { Translations } from "ui/Scripts/source/models/translations";
import { IListFilterItem, ListFilterItem } from "../../../models/common/listFilterItem";
import { ITypedOption } from "../../../models/common/typedOption";
import { IAppSessionState } from "../../../models/store/storeTypes";
import { HeaderMenuItem, NavigationMenu } from "../../app/NavigationMenu";
import { UserMenu } from "../../app/UserMenu";
import { PageContainer } from "../../framework/Page/PageContainer";
import { PageContent } from "../../framework/Page/PageContent";
import { PageTopBar } from "../../framework/Page/PageTopBar";
import { IPageNavMenuItem } from "../../framework/PageNav/PageNavMenuItems";
import { FilterTabs } from "../../framework/PageNavMenuItem/FilterTabs";
import { CalendarTaskSettingsDialog, CalendarTaskSettingsDialogMenuItem } from "../CalendarTaskSettingsDialog";
import { CalendarTaskPageCalendar } from "./CalendarTaskPageCalendar";
import { AppUtils } from "source/framework/appUtils";
import { userParameterCodeShowDocumentPreview } from "source/models/common/webConsts";
import { CalendarTaskCategoryNotSetId, CalendarTaskHandlersFilterAll, CalendarTaskStateFilterAll, getCalendarTaskPageFilterStateFromGeneralFilter, getCalendarTaskPageGeneralFilterFromFilterState, ICalendarTaskPageFilterLists, ICalendarTaskPageFilterState } from "./CalendarTaskPageUtils";
import { IdTitle, IIdTitle } from "ui/Scripts/source/models/common/idTitle";
import { ApplicationPermissionType, CalendarTaskPrivacyFilter, CalendarTaskState } from "ui/Scripts/source/models/enums";
import { IdColorTitle, IIdColorTitle } from "ui/Scripts/source/models/common/idColorTitle";
import { ICalendarTaskCategoryListItem } from "ui/Scripts/source/models/calendarTaskCategory/calendarTaskCategoryListItem";
import { ICodeTitle } from "source/models/common/codeTitle";
import { CalendarTaskPageFilterDrawer, calendarTaskFilterIsSet } from "./CalendarTaskPageFilterDrawer";
import { recordLockManager } from "source/models/services/recordLockService";
import { ListFilterSaveDialog } from "source/components/framework/Filters/ListFilterSaveDialog";
import { ISavedListFilter } from "source/models/common/savedListFilter";

// CalendarTaskPage
export interface ICalendarTaskPageOwnProps {
}

export interface ICalendarTaskPageStateProps {
    appState: IAppSessionState;
    dataId: number;
    calendarTaskPage: ICalendarTaskPage;
}

export interface ICalendarTaskPageDispatchProps {
    onGetCalendarTasksPage: () => void;
    onLogout: () => void;
    onSetCulture: (culture: string) => void;
    onSaveUserParameters: (parameters: ITypedOption[]) => void;
    onRefreshSessionState: () => void;
}

type CalendarTaskPageProp = ICalendarTaskPageOwnProps & ICalendarTaskPageStateProps & ICalendarTaskPageDispatchProps;

interface ICalendarTaskPageState extends ICalendarTaskPageFilterLists, ICalendarTaskPageFilterState {
    initialized: boolean;
    showNavPanel: boolean;
    activeCompanyId: string;
    sortColumn: string;
    sortOrderIsAsc: boolean;
    settingsActions: IPageNavMenuItem[];
    // Filters
    listFilterIsSet: boolean;
    showCalendarTaskFilterDrawer: boolean;
    calendarTaskFilterDrawerFilter: IListFilterItem;
    filters: IListFilterItem[];
    savedFilters: IListFilterItem[];
    activeFilterId: string;
    activeSavedFilterId: string;
    showListFilterSaveDialog: boolean;
    listFilterSaveDialogFilter: IListFilterItem;
    listFilterSaveDialogGeneralFilter: any;
    listFilterSaveDialogReservedNames: string[];
    listFilterSaveDialogOnGetGeneralFilter: () => any;
    // Properties
    dataId: number;
    showSettingsDialog: boolean;
    // Preview
    showDocumentPreview: boolean;
}

export class CalendarTaskPage extends React.Component<CalendarTaskPageProp, ICalendarTaskPageState> {
    private getStates(): IIdTitle[] {
        const result: IIdTitle[] = [];
        result.push(IdTitle.createIdTitle(CalendarTaskStateFilterAll.toString(10), "'" + Translations.All + "'"));
        return result.concat([
            IdTitle.createIdTitle(CalendarTaskState.NotStarted.toString(10), Translations.CalendarTaskStateNotStarted),
            IdTitle.createIdTitle(CalendarTaskState.Started.toString(10), Translations.CalendarTaskStateStarted),
            IdTitle.createIdTitle(CalendarTaskState.Finished.toString(10), Translations.CalendarTaskStateFinished),
        ]);
    }

    private getPrivacy(): IIdTitle[] {
        const result: IIdTitle[] = [];
        result.push(IdTitle.createIdTitle(CalendarTaskPrivacyFilter.All.toString(10), "'" + Translations.All + "'"));
        return result.concat([
            IdTitle.createIdTitle(CalendarTaskPrivacyFilter.Private.toString(10), Translations.Private),
            IdTitle.createIdTitle(CalendarTaskPrivacyFilter.Public.toString(10), Translations.Public),
        ]);
    }

    private getCategories(categories: ICalendarTaskCategoryListItem[]): IIdColorTitle[] {
        const result: IIdColorTitle[] = [];
        result.push(IdColorTitle.createIdColorTitle(Base.emptyGuid, "#fff", "'" + Translations.All + "'"));
        result.push(IdColorTitle.createIdColorTitle(CalendarTaskCategoryNotSetId, "#fff", "'" + Translations.NotSet + "'"));
        return result.concat(categories.map((item) => IdColorTitle.createIdColorTitle(item.id, item.color, item.code)));
    }
    
    private getHandlers = (items: ICodeTitle[]): IIdTitle[] => {
        const result: IIdTitle[] = [];
        result.push(IdTitle.createIdTitle(CalendarTaskHandlersFilterAll, "'" + Translations.All + "'"));
        return result.concat(items.map(i => IdTitle.createIdTitle(i.code, i.title)));
    };
    
    private getCreators = (items: ICodeTitle[]): IIdTitle[] => {
        const result: IIdTitle[] = [];
        result.push(IdTitle.createIdTitle(CalendarTaskHandlersFilterAll, "'" + Translations.All + "'"));
        return result.concat(items.map(i => IdTitle.createIdTitle(i.code, i.title)));
    };

    getFilters = (): IListFilterItem[] => {
        const result: IListFilterItem[] = [];
        result.push(ListFilterItem.createListFilterItem(Translations.Calendar, Translations.Calendar,
            {
            }, "startDate", true));
        ListFilterItem.sortListFilterItems(result);
        return result;
    };

    getSavedFilters = (props: CalendarTaskPageProp): IListFilterItem[] => {
        const result: IListFilterItem[] = [];
        if (!props || !props.calendarTaskPage) return result;
        for (const companyFilter of props.calendarTaskPage.companyFilters) {
            result.push(ListFilterItem.createListFilterItemFromSavedListFilter(companyFilter, true));
        }
        for (const userFilter of props.calendarTaskPage.userFilters) {
            result.push(ListFilterItem.createListFilterItemFromSavedListFilter(userFilter, false));
        }
        ListFilterItem.sortListFilterItems(result);
        return result;
    };

    getListFilterIsSet = (state: ICalendarTaskPageState): boolean => {
        return calendarTaskFilterIsSet(state, state);
    };

    getSettingsActions = (): IPageNavMenuItem[] => {
        const props = this.props;
        const sessionState = props.appState.state;
        const result: IPageNavMenuItem[] = [];
        if (sessionState.isCompanyAdmin) {
            result.push({ id: CalendarTaskSettingsDialogMenuItem.Permissions, title: Translations.Permissions });
            result.push({ id: CalendarTaskSettingsDialogMenuItem.Categories, title: Translations.CalendarTaskCategories });
        }
        if (sessionState.isSuperAdmin) {
            result.push({ id: CalendarTaskSettingsDialogMenuItem.DefaultCategories, title: Translations.DefaultCalendarTaskCategories });
        }
        return result;
    };

    constructor(props: CalendarTaskPageProp) {
        super(props);
        const sessionState = props.appState.state;
        const filters = this.getFilters();
        const filterProps = ListFilterItem.getFilterProps(filters[0], "", true, getCalendarTaskPageFilterStateFromGeneralFilter);
        const state = {
            ...filterProps,
            initialized: !!props.calendarTaskPage,
            showNavPanel: sessionState.getShowPageNavigation(),
            activeCompanyId: sessionState.companyId,
            settingsActions: this.getSettingsActions(),
            // ICalendarTaskPageFilterLists
            categories: this.getCategories(props.calendarTaskPage?.categories ?? []),
            states: this.getStates(),
            handlers: this.getHandlers(props.calendarTaskPage?.handlers ?? []),
            creators: this.getCreators(props.calendarTaskPage?.creators ?? []),
            privacy: this.getPrivacy(),
            // Filters
            listFilterIsSet: false,
            activeFilterId: filters[0].id,
            activeSavedFilterId: "",
            filters,
            savedFilters: this.getSavedFilters(props),
            showCalendarTaskFilterDrawer: false,
            calendarTaskFilterDrawerFilter: null,
            showListFilterSaveDialog: false,
            listFilterSaveDialogFilter: null,
            listFilterSaveDialogGeneralFilter: null,
            listFilterSaveDialogReservedNames: [],
            listFilterSaveDialogOnGetGeneralFilter: null,
            // Properties
            dataId: props.dataId,
            showSettingsDialog: false,
            // Preview
            showDocumentPreview: false
        };
        this.state = {
            ...state,
            listFilterIsSet: this.getListFilterIsSet(state)
        };
    }

    componentDidMount(): void {
        const sessionState = this.props.appState.state;
        recordLockManager.unlockAll(sessionState?.companyId, sessionState?.code);
        this.props.onGetCalendarTasksPage();
    }

    componentDidUpdate(prevProps: CalendarTaskPageProp, prevState: ICalendarTaskPageState): void {
        const props = this.props;
        const state = this.state;
        const sessionState = props.appState.state;
        //Companies or company changed?
        const prevCompanyIds = (prevProps.calendarTaskPage ? prevProps.calendarTaskPage.companies.map(i => i.id) : []).join(",");
        const companyIds = (props.calendarTaskPage ? props.calendarTaskPage.companies.map(i => i.id) : []).join(",");
        const prevCompanyId = prevProps.calendarTaskPage && prevProps.calendarTaskPage.companies.length > 0 ? prevProps.calendarTaskPage.companies[0].id : Base.emptyGuid;
        const companyId = props.calendarTaskPage && props.calendarTaskPage.companies.length > 0 ? props.calendarTaskPage.companies[0].id : Base.emptyGuid;
        //FilterLists changed?
        const categoriesData = props.calendarTaskPage?.categories ?? [];
        const equalCategories = IdTitle.equal(prevProps.calendarTaskPage?.categories ?? [], categoriesData);
        const handlersData = props.calendarTaskPage?.handlers ?? [];
        const equalHandlers = IdTitle.equal(prevProps.calendarTaskPage?.handlers ?? [], handlersData);
        const creatorsData = props.calendarTaskPage?.creators ?? [];
        const equalCreators = IdTitle.equal(prevProps.calendarTaskPage?.creators ?? [], creatorsData);
        if (prevProps.dataId === props.dataId && prevCompanyId === companyId && prevCompanyIds === companyIds &&
            equalCategories && equalHandlers && equalCreators) {
            return;
        }
        const savedFilters = this.getSavedFilters(props);
        const activeSavedFilterId = savedFilters.find(i => i.id === state.activeSavedFilterId)
            ? state.activeSavedFilterId
            : "";
        //FilterLists and related filters
        const categories = this.getCategories(categoriesData);
        const handlers = this.getHandlers(handlersData);
        const creators = this.getCreators(creatorsData);
        const selectedCategoryIds = equalCategories ? state.selectedCategoryIds : [Base.emptyGuid];
        const selectedHandlerUserGroupCodes = equalHandlers ? state.selectedHandlerUserGroupCodes : [CalendarTaskHandlersFilterAll];
        const stateChange = {
            dataId: props.dataId,
            initialized: !!props.calendarTaskPage,
            showNavPanel: state.initialized ? state.showNavPanel : sessionState.getShowPageNavigation(),
            activeCompanyId: companyId,
            savedFilters,
            activeSavedFilterId,
            categories,
            handlers,
            creators,
            selectedCategoryIds,
            selectedHandlerUserGroupCodes,
            showDocumentPreview: props.calendarTaskPage?.showDocumentPreview ?? state.showDocumentPreview
        };
        const newState = { ...state, ...stateChange };
        this.setState({
            ...stateChange,
            listFilterIsSet: this.getListFilterIsSet(newState),
            settingsActions: this.getSettingsActions(),
        });
    }

    handleToggleShowNavPanel = () => {
        this.setState({ showNavPanel: !this.state.showNavPanel });
    };

    // #region PageData
    handleRefreshPageData = async (companyId: string) => {
        const pageData = await calendarTaskService.getCalendarTaskPageData(companyId);
        if (!pageData) return;
        const state = this.state;
        //Categories
        const categories = this.getCategories(pageData.categories);
        const selectedCategoryIds = Base.keepAvailableSelectedIds(categories, state.selectedCategoryIds);
        //Handlers
        const handlers = this.getHandlers(pageData.handlers);
        const selectedHandlerUserGroupCodes = Base.keepAvailableSelectedIds(handlers, state.selectedHandlerUserGroupCodes, "");
        //Creators
        const creators = this.getCreators(pageData.creators);
        const selectedCreateUserCodes = Base.keepAvailableSelectedIds(creators, state.selectedCreateUserCodes, "");
        //State
        const stateChange = {
            activeCompanyId: companyId,
            categories,
            handlers,
            creators,
            selectedCategoryIds,
            selectedHandlerUserGroupCodes,
            selectedCreateUserCodes,
            settingsActions: this.getSettingsActions()
        };
        const newState = { ...state, ...stateChange };
        this.setState({
            ...stateChange,
            listFilterIsSet: this.getListFilterIsSet(newState)
        });
    };
    // #endregion PageData

    handleChangeActiveCompanyId = async (companyId: string) => {
        if (this.state.activeCompanyId === companyId) return;
        await this.handleRefreshPageData(companyId);
    };

    // #region Saved Filter
    getFilterPropsWithState = (listItemFilter: IListFilterItem) => {
        const state = this.state;
        let result = {
            selectedCategoryIds: state.selectedCategoryIds,
            selectedStateIds: state.selectedStateIds,
            selectedHandlerUserGroupCodes: state.selectedHandlerUserGroupCodes,
            selectedPrivacy: state.selectedPrivacy,
            sortColumn: state.sortColumn,
            sortOrderIsAsc: state.sortOrderIsAsc
        };
        if (listItemFilter) {
            result = ListFilterItem.getFilterProps(listItemFilter, result.sortColumn, result.sortOrderIsAsc, getCalendarTaskPageFilterStateFromGeneralFilter);
        }
        return result;
    };

    getStateChangeAfterChangeActiveFilter = (activeFilterId: string): any => {
        const state = this.state;
        const filter = state.filters.find(i => i.id === activeFilterId);
        if (!filter) return {};
        const filterProps = this.getFilterPropsWithState(filter);
        const activeSavedFilterId = "";
        const newState = { ...state, ...filterProps, activeFilterId, activeSavedFilterId };
        return {
            ...filterProps,
            activeFilterId,
            activeSavedFilterId: "",
            listFilterIsSet: this.getListFilterIsSet(newState)
        };
    };

    handleChangeActiveFilter = (activeFilterId: string) => {
        this.setState(this.getStateChangeAfterChangeActiveFilter(activeFilterId));
    };

    getStateChangeAfterChangeActiveSavedFilter = (activeSavedFilterId: string): any => {
        const state = this.state;
        const savedFilter = state.savedFilters.find(i => i.id === activeSavedFilterId);
        if (!savedFilter) return {};
        const filterProps = this.getFilterPropsWithState(savedFilter);
        const activeFilterId = "";
        const newState = { ...state, ...filterProps, activeFilterId, activeSavedFilterId };
        return {
            ...filterProps,
            activeFilterId,
            activeSavedFilterId,
            listFilterIsSet: this.getListFilterIsSet(newState)
        };
    };

    handleChangeActiveSavedFilter = (activeSavedFilterId: string) => {
        this.setState(this.getStateChangeAfterChangeActiveSavedFilter(activeSavedFilterId));
    };

    handleRemoveSavedFilter = async (savedFilterId: string) => {
        const state = this.state;
        const savedFilter = state.savedFilters.find(i => i.id === savedFilterId);
        if (!savedFilter) return;
        const result = await ListFilterSaveDialog.removeListFilterFromDb(state.savedFilters, state.activeCompanyId, savedFilterId, savedFilter.isShared, ApplicationPermissionType.CalendarTask);
        if (!result) return;
        const savedFilters = state.savedFilters.filter(i => i.id !== savedFilterId);
        let activeFilterId = "";
        let activeSavedFilterId = "";
        let newfilter: IListFilterItem = null;
        if (savedFilters.length > 0) {
            newfilter = savedFilters[0];
            activeSavedFilterId = newfilter.id;
        } else if (state.filters.length > 0) {
            newfilter = state.filters[0];
            activeFilterId = newfilter.id;
        }
        const filterProps = this.getFilterPropsWithState(newfilter);
        const newState = { ...state, ...filterProps, activeFilterId, activeSavedFilterId, savedFilters };
        this.setState({
            ...filterProps,
            showCalendarTaskFilterDrawer: false,
            activeFilterId,
            activeSavedFilterId,
            savedFilters,
            listFilterIsSet: this.getListFilterIsSet(newState),
        });
    };

    handleFilterSortData = (sortColumn: string, sortOrderIsAsc: boolean) => {
        this.setState({
            sortColumn,
            sortOrderIsAsc,
        });
    };

    updateFiltersAfterSave = (orgFilterId: string, savedListFilter: ISavedListFilter, isShared: boolean) => {
        if (!savedListFilter) return;
        const state = this.state;
        const savedFilters = state.savedFilters.filter(i => i.id !== orgFilterId || i.isShared !== isShared);
        const filter = ListFilterItem.createListFilterItemFromSavedListFilter(savedListFilter, isShared);
        savedFilters.push(filter);
        ListFilterItem.sortListFilterItems(savedFilters);
        this.setState({
            showListFilterSaveDialog: false,
            savedFilters,
            activeFilterId: "",
            activeSavedFilterId: savedListFilter.id,
            calendarTaskFilterDrawerFilter: filter
        });
    };

    getActiveFilter = (): IListFilterItem => {
        const state = this.state;
        return state.activeFilterId
            ? state.filters.find(i => i.id === state.activeFilterId)
            : state.savedFilters.find(i => i.id === state.activeSavedFilterId);
    };

    handleSaveListFilter = async (saveAs: boolean, onGetGeneralFilter: () => any) => {
        const state = this.state;
        const filter = this.getActiveFilter();
        if (!filter) return;
        if (!saveAs) {
            const savedListFilter = await ListFilterSaveDialog.saveListFilterToDb(filter, getCalendarTaskPageGeneralFilterFromFilterState(state), state.sortColumn, state.sortOrderIsAsc, state.activeCompanyId, ApplicationPermissionType.CalendarTask);
            this.updateFiltersAfterSave(filter.id, savedListFilter, filter.isShared);
        } else {
            this.setState({
                showListFilterSaveDialog: true,
                listFilterSaveDialogFilter: filter,
                listFilterSaveDialogReservedNames: state.savedFilters.map(i => i.id).concat(state.filters.map(i => i.title)),
                listFilterSaveDialogGeneralFilter: getCalendarTaskPageGeneralFilterFromFilterState(state),
                listFilterSaveDialogOnGetGeneralFilter: onGetGeneralFilter
            });
        }
    };

    handleSaveListFilterDialogOk = (savedListFilter: ISavedListFilter, isShared: boolean) => {
        this.updateFiltersAfterSave("", savedListFilter, isShared);
    };

    handleSaveListFilterDialogCancel = () => {
        this.setState({
            showListFilterSaveDialog: false
        });
    };
    // #endregion Saved Filter

    // #region CalendarTask Filter
    handleOpenCalendarTaskFilterDrawer = () => {
        const filter = this.getActiveFilter();
        if (!filter) return;
        this.setState({
            showCalendarTaskFilterDrawer: true,
            calendarTaskFilterDrawerFilter: filter
        });
    };

    handleCalendarTaskFilterDrawerOk = (closeDialog: boolean, newFilterState: ICalendarTaskPageFilterState): any => {
        const newState = {
            ...this.state,
            ...newFilterState
        };
        this.setState({
            showCalendarTaskFilterDrawer: !closeDialog,
            ...newFilterState,
            dataId: this.state.dataId + 1,
            listFilterIsSet: this.getListFilterIsSet(newState)
        });
        return getCalendarTaskPageGeneralFilterFromFilterState(newState);
    };
    
    handleCalendarTaskFilterDrawerCancel = () => {
        this.setState({ showCalendarTaskFilterDrawer: false });
    };
    // #endregion CalendarTask Filter

    handleShowDetailsPanel = (showDocumentPreview) => {
        this.setState({ showDocumentPreview });
        AppUtils.callService(() => userService.saveUserParameter(userParameterCodeShowDocumentPreview, showDocumentPreview ? "TRUE" : "FALSE"), false, true);
    };
    
    handleOpenSettingsDialog = async () => {
        this.setState({
            showSettingsDialog: true
        });
    };

    handleSettingsDialogOk = () => {
        this.setState({
            dataId: Base.getRandomIntInteger(1000000),
            showSettingsDialog: false
        });
        this.handleRefreshPageData(this.state.activeCompanyId);
    };

    render() {
        const props = this.props;
        const state = this.state;
        const sessionState = props.appState.state;
        const companies = props.calendarTaskPage ? props.calendarTaskPage.companies : [];
        return (
            <PageContainer>
                <NavigationMenu
                    appState={props.appState}
                    activeMenuItem={HeaderMenuItem.CalendarTasks}
                    companies={companies}
                    activeCompanyId={state.activeCompanyId}
                    onChangeActiveCompany={this.handleChangeActiveCompanyId}
                />
                <PageContent>
                    <PageTopBar>
                        <UserMenu
                            appState={props.appState}
                            onLogout={props.onLogout}
                            onSetCulture={props.onSetCulture}
                            onRefreshSessionState={props.onRefreshSessionState}
                        />
                    </PageTopBar>
                    <CalendarTaskPageCalendar
                        companyId={state.activeCompanyId}
                        settingsVisible={state.settingsActions.length > 0}
                        savedFilter={state.activeSavedFilterId ? state.savedFilters.find(i => i.id === state.activeSavedFilterId) : null}
                        startDate={props.calendarTaskPage?.calendar?.startDate}
                        endDate={props.calendarTaskPage?.calendar?.endDate}
                        initialItems={props.calendarTaskPage?.calendar?.items ?? []}
                        initialPrevItem={props.calendarTaskPage?.calendar?.prevItem}
                        initialNextItem={props.calendarTaskPage?.calendar?.nextItem}
                        selectedCategoryIds={state.selectedCategoryIds}
                        selectedStateIds={state.selectedStateIds}
                        selectedHandlerUserGroupCodes={state.selectedHandlerUserGroupCodes}
                        selectedCreateUserCodes={state.selectedCreateUserCodes}
                        selectedPrivacy={state.selectedPrivacy}
                        viewFilterIsSet={state.listFilterIsSet}
                        showDocumentPreview={state.showDocumentPreview}
                        reportTabs={(
                            <FilterTabs
                                items={state.savedFilters}
                                standardFilters={state.filters}
                                selectedId={this.state.activeFilterId}
                                selectedSavedId={this.state.activeSavedFilterId}
                                onSetActiveItem={this.handleChangeActiveSavedFilter}
                                onSetActiveStandardItem={this.handleChangeActiveFilter}
                            />
                        )}
                        sessionState={sessionState}
                        onToggleSettingsDialog={this.handleOpenSettingsDialog}
                        onOpenFilterDrawer={this.handleOpenCalendarTaskFilterDrawer}
                        onChangeShowDocumentPreview={this.handleShowDetailsPanel}
                    />
                </PageContent>
                {state.showSettingsDialog &&
                    <CalendarTaskSettingsDialog
                        sessionState={sessionState}
                        companyId={state.activeCompanyId}
                        settingsActions={state.settingsActions}
                        onClose={this.handleSettingsDialogOk}
                    />
                }
                <CalendarTaskPageFilterDrawer
                    open={state.showCalendarTaskFilterDrawer}
                    filter={state.calendarTaskFilterDrawerFilter}
                    categories={state.categories}
                    states={state.states}
                    handlers={state.handlers}
                    creators={state.creators}
                    privacy={state.privacy}
                    selectedCategoryIds={state.selectedCategoryIds}
                    selectedStateIds={state.selectedStateIds}
                    selectedHandlerUserGroupCodes={state.selectedHandlerUserGroupCodes}
                    selectedCreateUserCodes={state.selectedCreateUserCodes}
                    selectedPrivacy={state.selectedPrivacy}
                    canRemoveSharedFilters={sessionState.isCompanyAdmin}
                    onSaveFilter={this.handleSaveListFilter}
                    onRemoveFilter={this.handleRemoveSavedFilter}
                    onOk={this.handleCalendarTaskFilterDrawerOk}
                    onCancel={this.handleCalendarTaskFilterDrawerCancel}
                />
                {state.showListFilterSaveDialog &&
                    <ListFilterSaveDialog
                        sessionState={sessionState}
                        companyId={state.activeCompanyId}
                        applicationPermissionType={ApplicationPermissionType.CalendarTask}
                        listFilter={state.listFilterSaveDialogFilter}
                        generalFilter={state.listFilterSaveDialogGeneralFilter}
                        sortColumn={state.sortColumn}
                        sortOrderIsAsc={state.sortOrderIsAsc}
                        reservedNames={state.listFilterSaveDialogReservedNames}
                        onGetGeneralFilter={state.listFilterSaveDialogOnGetGeneralFilter}
                        onOk={this.handleSaveListFilterDialogOk}
                        onCancel={this.handleSaveListFilterDialogCancel}
                    />
                }
            </PageContainer>
        );
    }
}